import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { INumericInputProps } from '..';
import { withInjectedProps } from '../InjectedPropsContext';

interface ITimerReadingInputProps extends INumericInputProps {
  maxLength?: number;
}

const TimerReadingInputBase = ({
  disabled,
  id,
  name,
  onChange,
  onKeyDown,
  severity,
  value,
  onBlur,
  maxLength = 8,
  focusRef,
  onFocus,
}: ITimerReadingInputProps) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const [timerValue, setInputValue] = useState<any>();
  //Defines a maximum value for each index.
  //E.g. the first value must be
  let maxValues = new Map<number, number>([
    [0, 2],
    [3, 5],
    [6, 5],
  ]);

  let maxValuesGroup = new Map<number, number>([
    [0, 23],
    [1, 59],
    [3, 59],
  ]);

  const [cursor, setCursor] = useState(null);

  useEffect(() => {
    const input = ref.current;
    //This should re-position the cursor so it dosen't skip to the end of the input range.
    if (input) {
      if (!lastCharacterIsColon(value)) input.setSelectionRange(cursor, cursor);
    }
  }, [ref, cursor, value]);

  /**
   * helper function to check if the final character is a colon or not.
   */
  const lastCharacterIsColon = (val: string) => {
    if (val) {
      return val !== (null || undefined) && val.toString().lastIndexOf(':') === val.length - 1;
    }
  };

  const formatText = (inputValue: string): string => {
    //Validate user input.
    //Check if any numeric value is above its max allowed value. Remove it if it's not. (i,e no entry)
    inputValue = inputValue
      .split('')
      .filter((char, index) => {
        const num = parseInt(char);
        let maxValue = maxValues.get(index);
        return char === ':' || maxValue === undefined || num <= maxValue;
      })
      .join('');

    inputValue = inputValue
      .split('^[0-9]{2}|:')
      .map((char, index) => {
        const num = parseInt(char);
        let maxValue = maxValuesGroup.get(index);
        if (char === ':' || maxValue === undefined || num <= maxValue) return char;
        return char[0];
      })
      .join('');
    setInputValue(inputValue);

    //If the last entered or formatted value is a colon.
    //Simply return that. We don't need to do any further formatting
    if (lastCharacterIsColon(inputValue) || lastCharacterIsColon(value)) {
      return inputValue;
    }

    // Remove any non-numeric characters
    const cleanedText = inputValue.replace(/[^0-9]/g, '');

    // Generates a regex expression to format the text against.
    // This should basically format the text as you type, to insert colons at the required spots.
    const formattedText = constructRegex(cleanedText);
    setInputValue(formattedText);
    return formattedText;
  };

  const constructRegex = (text: string) => {
    let textLength = Math.floor(text.length / 2); //Divide by 2. So we basically treat the text as segments
    let replaceString = '';

    //Generate searchString for regex parameters
    let searchString = '(\\d{2})'.repeat(textLength);

    //Generate replaceString  for regex parameters
    for (let i = 0; i < textLength; i++) {
      let a = '$' + (i + 1) + ':';
      replaceString = replaceString + a;
    }
    //remove any trailing colon from the end of the replace string
    if (textLength === 3) {
      replaceString = replaceString.replace(/:$/, '');
    }

    let regex = new RegExp(searchString);

    //Format text
    return text.replace(regex, replaceString);
  };

  const InvokeOnChange = (e: any) => {
    /**
     * Handle blur (lost focus)
     * If the user loses and the value is not valid, simply pad the remainder of the value.
     */
    setCursor(e.target.selectionStart);
    onChange && onChange(e);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = formatText(event.target.value);
    (event as any).data = { name, value: newValue, submitValues: false };
    InvokeOnChange?.(event);
  };

  /**
   * Handle blur (lost focus)
   * If the user loses and the value is not valid, simply pad the remainder of the value.
   */
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    let value = ((event as any).target.value = checkIfTimeIsInvalid(formatText((event as any).target.value)));
    (event as any).data = { name, value: value, submitValues: true };
    setInputValue(value);
    if (onChange) {
      InvokeOnChange(event as any);
    }
    onBlur?.(event);
  };

  /**
   * Handle Key Down
   * If the user hits enter and the value is not valid, simply pad the remainder of the value.
   */
  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      let value = formatText((event.target as HTMLInputElement).value);
      value = checkIfTimeIsInvalid(value);
      (event as any).data = {
        name,
        value: value,
        submitValues: true,
      };
      setInputValue(value);
      event.preventDefault();
      event.target.blur(); // Lose focus after Enter is pressed
      InvokeOnChange?.(event as any);
    }

    onKeyDown?.(event);
  };

  const handleFocus = (_event: React.FocusEvent<HTMLInputElement>) => {
    if (ref.current) {
      ref.current.style.display = '';
      ref.current.focus();
      ref.current.select();
    }
    if (focusRef) (focusRef as any).current = ref.current;
    onFocus?.(_event);
  };

  /**
   * Checks if the time is valid
   * If the user hits enter and the value is not valid, simply pad the remainder of the value and format.
   */
  const checkIfTimeIsInvalid = (value: string) => {
    //Allow null values or empty strings
    if (value === undefined || value === null || value.length === 0) {
      return value;
    }

    //Check if time is below the maximum.
    //If it's not, pad and format the rest of the value so its formatted as ##:##:##
    if (value.length < maxLength) {
      const parts = value.split(':');
      const hours = parts[0] || '00';
      const minutes = parts[1] || '00';
      const seconds = parts[2] || '00';

      const formattedValue = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
      setInputValue(formattedValue);
      return formattedValue;
    } else {
      return value;
    }
  };

  // Sync value to display from props unless user has made local changes
  // Local changes flagged through handleChange and then synced through handleBlur
  if (value !== undefined && timerValue !== value) {
    setInputValue(value);
  }

  const cx = classNames('TextInput', { [`Input-${severity}`]: !!severity });
  return (
    <input
      className={cx}
      data-testid={id}
      disabled={disabled}
      id={id}
      placeholder="00:00:00"
      name={name}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      inputMode="decimal"
      onFocus={(e) => {
        handleFocus(e);
      }}
      ref={focusRef}
      type="text"
      maxLength={maxLength}
      value={value === undefined ? '' : timerValue ?? ''}
    />
  );
};
const TimerReadingInput = withInjectedProps(TimerReadingInputBase);

export { TimerReadingInput };
export default TimerReadingInput;
